'use strict';
import { cn_mmm_element } from '../model/cn_mmm_element';
import { cn_edition_handler } from './cn_edition_handler';
import { cn_svg_map } from './cn_svg_map';
import { cn_3d_line_handler } from './cn_3d_line_handler';
import { fh_clone } from '@acenv/fh-3d-viewer';
import { cn_mouse_event } from './cn_mouse_event';
import { cn_type_pastille } from './cn_type_pastille';
import { cn_edit_box } from './cn_edit_box';
import { cn_number_input } from './cn_inputs';
import { cn_handler_wall_distance } from './cn_handler_wall_distance';
import { cn_polygon_handler } from './cn_polygon_handler';
import { cn_add, cn_clone } from '../utils/cn_utilities';
import { cn_pastille } from './cn_pastille';

export class cn_mmm_handler extends cn_edition_handler {
    //***********************************************************************************
    /**
     * Constructor
     * @param {Array<cn_mmm_element>} mmm_elements
     * @param {cn_svg_map} map
     * @param {boolean} creation
     */
    constructor(mmm_elements, map, creation = false) {
        super(mmm_elements, map);

        this._map = map;
        this._scene = this._map._scene;
        this._transaction_manager = this._map._building.transaction_manager;

        //*** handler for shape */
        this._handler = null;

        //*** mmm_element
        this.mmm_elements = mmm_elements;
        this._mmm_element = (mmm_elements.length > 1) ? null : mmm_elements[0];
        if (this._mmm_element) this._initialize_edition();

        this._current_vertex_index = 0;

        //*** Edit box for mass selection */
        const edit_box = new cn_edit_box(this, mmm_elements, this._readOnly);
        this._handlers.push(edit_box);

        let self = this;
        if (!this._readOnly) {
            //*** Type pastille */
            const type_pastille = new cn_type_pastille(mmm_elements, 'composition', () => map._building.get_mmm_compositions(), `Composition d'élément générique`, map);
            type_pastille.title = `Modifier le type d'élément générique`;
            edit_box.add_pastille(type_pastille);

            let width_mmm_elements = mmm_elements.filter(mmm => mmm.vertices.length < 3);
            if (width_mmm_elements.length) {
                const width_pastille = new cn_pastille([0, 0], 'arrow_left_right.svg');
                edit_box.add_pastille(width_pastille);
                width_pastille.svg_class = 'pastille_background white';
                width_pastille.title = 'Taille de l\'élément';
                width_pastille.clicked = () => {
                    const input = new cn_number_input('Taille d\'élément générique', width_mmm_elements[0].width, 'm', 2, 0.01, 10);
                    if (width_mmm_elements.some(mmm => mmm.width !== width_mmm_elements[0].width)) input.label += ' (variable)';
                    input.callback = () => {
                        self._transaction_manager.push_transaction('Taille d\'élément générique');
                        const new_value = (input.checkbox_status) ? 0 : input.value;
                        width_mmm_elements.forEach(mmm => {
                            self._transaction_manager.push_item_set(mmm, ['width'])
                            mmm.width = new_value;
                        });
                        map.refresh();
                    };
                    map.call('number_input', input);
                }
            }

            edit_box.add_lock_pastille(this._transaction_manager);
        }

        edit_box.add_select_siblings_pastille('composition');
        edit_box.add_information_pastille('composition');
    }

    //***********************************************************************************
    //**** Draws
    //***********************************************************************************
    draw(camera) {
        var html = '';

        if (this._mmm_element) html += this._mmm_element.draw(camera, ['selected']);

        if (this._handler)
            this._handler.active = this._handler.visible = !this._mmm_element.locked;

        html += super.draw(camera);

        return html;
    }

    //***********************************************************************************
    //**** clear move effects
    //***********************************************************************************
    /**
     * Manage a passive move. To return 'true' if something of interest under the mouse.
     * @param {cn_mouse_event} ev
     * @returns  {boolean}
     */
    move(ev) {
        if (super.move(ev)) return true;

        return this._mmm_element && this._mmm_element.contains(ev.mouse_world, ev.camera.snap_world_distance);
    }

    _initialize_edition() {
        this._handler = null;

        this._distance_handler = new cn_handler_wall_distance(this._scene, this._map);
        this._handlers.push(this._distance_handler);
        this._distance_handler.from_contour(this._mmm_element.vertices);

        this._handler = new cn_polygon_handler(this,this._mmm_element.vertices,true,this._mmm_element.vertices.length > 2);
        this._handler.snap_elements = this._scene.spaces;
        this._handlers.push(this._handler);

        if (this._map.get_readonly()) {
            return;
        }

        //*** Callback on change */
        this._handler.on('change', () => {
            this._transaction_manager.push_transaction(`Modification d'élément générique`, this._mmm_element.ID);
            this._transaction_manager.push_item_set(this._mmm_element, 'vertices');
            this._mmm_element.vertices=this._handler.vertices.map(v => cn_clone(v));
            this._distance_handler.from_contour(this._mmm_element.vertices);
            this._map.refresh();
        });
        
        this._distance_handler.on("change", (offset) => {
            this._transaction_manager.push_transaction(`Modification d'élément générique`, this._mmm_element.ID);
            this._transaction_manager.push_item_set(this._mmm_element, 'vertices');
            this._mmm_element.vertices = this._mmm_element.vertices.map(v => cn_add(v,offset));
            this._distance_handler.from_contour(this._mmm_element.vertices);
            this._handler.vertices = this._mmm_element.vertices;
            this._handler.update();
            this._map.refresh();
        });
    }
}

